import React, { useState, useContext } from "react";
import "../../App.css"; // Import global styles
import { UserContext } from "../context/UserContext"; // Import UserContext for user authentication

const AddTutorialVideo = ({ onClose, video = null, handleSubmit = null }) => {
  const { emailId } = useContext(UserContext);
  // Initialize state with video data if editing
  const [videoTitle, setVideoTitle] = useState(video ? video.title : "");
  const [description, setDescription] = useState(video ? video.description : "");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState(video ? video.media_type : null);
  const [currentMediaUrl, setCurrentMediaUrl] = useState(video ? video.video_url : null);
  const [isMediaChanged, setIsMediaChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Validation function for media files
  const validateFile = (file) => {
    const allowedImageTypes = ["image/jpeg", "image/png", "image/gif"];
    const allowedVideoTypes = ["video/mp4", "video/quicktime", "video/x-msvideo"];
    const maxSize = 500 * 1024 * 1024; // 500MB

    if (![...allowedImageTypes, ...allowedVideoTypes].includes(file.type)) {
      alert("Invalid file type. Only images (JPG, PNG, GIF) and videos (MP4, MOV, AVI) are allowed.");
      return false;
    }

    if (file.size > maxSize) {
      alert("File is too large. Maximum size is 500MB.");
      return false;
    }

    return true;
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setMediaFile(file);
      setMediaType(file.type);
      setIsMediaChanged(true);
    }
  };

  // Upload media to S3
  const uploadMediaToS3 = async (file) => {
    const formData = new FormData();
    formData.append("media", file);

    try {
      const response = await fetch("/api/upload-s3", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Media upload failed");

      const data = await response.json();
      return { url: data.mediaUrl, type: data.mediaType };
    } catch (error) {
      console.error("Error uploading media:", error);
      throw error;
    }
  };

  // Handle form submission
  const handleSaveTutorial = async () => {
    if (!videoTitle || !description || (!mediaFile && !currentMediaUrl)) {
      alert("Please provide a title, description, and upload a video or image!");
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(10); // Start with 10% progress
    
    try {
      let mediaData = { url: currentMediaUrl, type: mediaType };
      
      if (isMediaChanged && mediaFile) {
        setUploadProgress(30); // Update progress before media upload
        mediaData = await uploadMediaToS3(mediaFile);
        setUploadProgress(70); // Update progress after media upload
      }

      const formData = {
        title: videoTitle,
        description: description,
        video_url: mediaData.url,
        media_type: mediaData.type,
        email_id: emailId,
      };

      setUploadProgress(80); // Update progress before final submission

      if (video) {
        await handleSubmit(formData);
      } else {
        const response = await fetch("/api/tutorialvideos", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error("Failed to add tutorial video");
        }
        setUploadProgress(100); // Set to 100% on successful completion
        alert("✅ Tutorial video added successfully!");
      }
      
      // Add a small delay before closing to show completion
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("🚨 Error submitting form:", error);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      // Add a small delay before closing the progress bar
      setTimeout(() => {
        setIsSubmitting(false);
        setUploadProgress(0);
      }, 1000);
    }
  };

  return (
    <div
      className="popup-section"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999,
      }}
    >
      <div
        className="popup-container"
        style={{
          backgroundColor: "#fff",
          borderRadius: "10px",
          padding: "20px",
          width: "95%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          position: "relative",
        }}
      >
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          {video ? "Edit Tutorial Video" : "Add Tutorial Video"}
        </h3>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            cursor: "pointer",
          }}
        >
          &times;
        </button>

        {/* Tutorial Video Form */}
        <form>
          {/* Video Title */}
          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="videoTitle"
              style={{
                display: "block",
                fontSize: "14px",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Video Title:
            </label>
            <input
              id="videoTitle"
              type="text"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              placeholder="Enter video title"
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          {/* Description Field ✅ Added */}
          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="description"
              style={{
                display: "block",
                fontSize: "14px",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Description:
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter video description"
              rows="4"
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                resize: "none",
              }}
            />
          </div>

          {/* Media Upload/Preview */}
          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="mediaUpload"
              style={{
                display: "block",
                fontSize: "14px",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              {video ? "Change Media (Optional):" : "Upload Image or Video (Max 500MB):"}
            </label>
            <input
              id="mediaUpload"
              type="file"
              accept="image/jpeg, image/png, image/gif, video/mp4, video/quicktime, video/x-msvideo"
              onChange={handleFileChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
              }}
            />

            {/* Add YouTube URL input for admin */}
            {emailId === "urvote_tutorials@urcommunity.com.au" && (
              <div style={{ marginTop: "10px" }}>
                <label
                  htmlFor="youtubeUrl"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  Or Paste YouTube Video URL:
                </label>
                <input
                  id="youtubeUrl"
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={currentMediaUrl || ""}
                  onChange={(e) => {
                    setCurrentMediaUrl(e.target.value);
                    setMediaType("video/youtube");
                    setIsMediaChanged(true);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            )}
            
            {/* Preview current or new media */}
            <div style={{ marginTop: "10px" }}>
              {mediaFile ? (
                // Preview new file
                mediaType?.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(mediaFile)}
                    alt="Preview"
                    style={{ maxWidth: "200px" }}
                  />
                ) : (
                  <video controls style={{ maxWidth: "200px" }}>
                    <source src={URL.createObjectURL(mediaFile)} type={mediaType} />
                    Your browser does not support the video tag.
                  </video>
                )
              ) : currentMediaUrl ? (
                // Show existing media
                mediaType?.startsWith("image/") ? (
                  <img
                    src={currentMediaUrl}
                    alt="Current media"
                    style={{ maxWidth: "200px" }}
                  />
                ) : (
                  <video controls style={{ maxWidth: "200px" }}>
                    <source src={currentMediaUrl} type={mediaType} />
                    Your browser does not support the video tag.
                  </video>
                )
              ) : null}
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
            <button
              type="button"
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              onClick={handleSaveTutorial}
            >
              {video ? "Save Changes" : "Submit"}
            </button>
          </div>
        </form>

        {isSubmitting && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "10px",
                textAlign: "center",
                width: "300px",
              }}
            >
              <div style={{ marginBottom: "20px" }}>
                <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
                  Uploading your tutorial...
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${uploadProgress}%`,
                      height: "100%",
                      backgroundColor: "#4CAF50",
                      borderRadius: "10px",
                      transition: "width 0.3s ease-in-out",
                    }}
                  />
                </div>
                <div style={{ marginTop: "10px", fontSize: "14px" }}>
                  {uploadProgress}% Complete
                </div>
              </div>
              <div style={{ color: "#666", fontSize: "14px" }}>
                Please wait while we process your tutorial...
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddTutorialVideo;
